import { createAsyncThunk } from '@reduxjs/toolkit';
import { AppServerResponse } from '../../utils/interfaces/generalInterfaces';
import axios from 'axios';
import { BASE_API_URL } from '../../utils/constants';
import { myFirebaseAuth } from '../../firebase/config';
import { logoutUser } from './authService';
import {
  SalesRepCreate,
  SalesRepUpdate,
  SupplierBranch,
  TaxExemptionTeamMemberCreate,
  TaxExemptionTeamMemberUpdate,
} from '@dill/dill-shared';

export const getSupplierSalesReps = createAsyncThunk(
  'salesReps/getSupplierSalesReps',
  async ({ supplierId }: { supplierId: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/salesReps`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const createSalesRep = createAsyncThunk(
  'salesReps/createSalesRep',
  async ({ supplierId, data }: { supplierId: string; data: SalesRepCreate }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/salesReps`,
        data,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.response?.data ?? error.message);
    }
  }
);

export const updateSalesRep = createAsyncThunk(
  'salesReps/updateSalesRep',
  async (
    {
      supplierId,
      salesRepId,
      data,
    }: { supplierId: string; salesRepId: string; data: SalesRepUpdate },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.put<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/salesReps/${salesRepId}`,
        data,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.response?.data ?? error.message);
    }
  }
);

export const deleteSalesRep = createAsyncThunk(
  'salesReps/deleteSalesRep',
  async ({ supplierId, salesRepId }: { supplierId: string; salesRepId: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.delete<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/salesReps/${salesRepId}`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.response?.data ?? error.message);
    }
  }
);

export const getSupplierTaxExemptionTeam = createAsyncThunk(
  'taxExemptionTeam/getSupplierTaxExemptionTeam',
  async ({ supplierId }: { supplierId: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/taxExemptionTeam`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const createTaxExemptionTeamMember = createAsyncThunk(
  'taxExemptionTeam/createTaxExemptionTeamMember',
  async (
    { supplierId, data }: { supplierId: string; data: TaxExemptionTeamMemberCreate },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/taxExemptionTeam`,
        data,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.response?.data ?? error.message);
    }
  }
);

export const updateTaxExemptionTeamMember = createAsyncThunk(
  'taxExemptionTeam/updateTaxExemptionTeamMember',
  async (
    {
      supplierId,
      teamMemberId,
      data,
    }: { supplierId: string; teamMemberId: string; data: TaxExemptionTeamMemberUpdate },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.put<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/taxExemptionTeam/${teamMemberId}`,
        data,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.response?.data ?? error.message);
    }
  }
);

export const getSupplierBranches = createAsyncThunk(
  'supplierBranches/getSupplierBranches',
  async ({ supplierId }: { supplierId: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/branches`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const updateSupplierBranches = createAsyncThunk(
  'supplierBranches/updateSupplierBranches',
  async (
    { supplierId, branches }: { supplierId: string; branches: SupplierBranch[] },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.put<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/branches`,
        { branches },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.response?.data ?? error.message);
    }
  }
);

export const deleteTaxExemptionTeamMember = createAsyncThunk(
  'taxExemptionTeam/deleteTaxExemptionTeamMember',
  async ({ supplierId, teamMemberId }: { supplierId: string; teamMemberId: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.delete<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/taxExemptionTeam/${teamMemberId}`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.response?.data ?? error.message);
    }
  }
);

// `paymentProviderDetails` lives on the supplierAdditionalDetails subcollection now, not the
// Supplier doc itself (see supplierAdditionalDetailsSchema.ts). The API only ever returns this
// narrow, credential-free shape - `userName`/`password` never leave the server (see
// getSupplierPaymentProviderDetailsController in supplierAdditionalDetailsControllers.ts).
export interface SupplierFiservPaymentProviderDetails {
  merchantId: string | null;
  merchantConnectionUrl: string | null;
  isFullySetup: boolean;
}

export const getSupplierPaymentProviderDetails = createAsyncThunk(
  'supplierPaymentProviderDetails/getSupplierPaymentProviderDetails',
  async ({ supplierId }: { supplierId: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/fiserv`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.response?.data ?? error.message);
    }
  }
);

export const updateSupplierPaymentProviderDetails = createAsyncThunk(
  'supplierPaymentProviderDetails/updateSupplierPaymentProviderDetails',
  async (
    {
      supplierId,
      fiserv,
    }: {
      supplierId: string;
      fiserv: { merchantId?: string; userName?: string; password?: string };
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.put<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/fiserv`,
        { fiserv },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.response?.data ?? error.message);
    }
  }
);

// The browser's only source of a supplier's integration state. Deliberately a secrets-free
// projection served by GET /suppliers/:supplierId/integrationDetails - see
// RedactedSupplierIntegrationDetailsSchema. Previously the UI read `supplier.integrationDetails`
// straight off the auth payload, which carried live ERP credentials into the browser.
export const getSupplierIntegrationDetails = createAsyncThunk(
  'supplierIntegrationDetails/getSupplierIntegrationDetails',
  async ({ supplierId }: { supplierId: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/integrationDetails`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const saveP21ManualUploadSettingsService = createAsyncThunk(
  'supplierIntegrationDetails/saveP21ManualUploadSettings',
  async (
    {
      supplierId,
      settings,
    }: {
      supplierId: string;
      settings: {
        baseUrl: string;
        userName: string;
        password: string;
        isAutomatedUploadEnabled: boolean;
      };
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/integrationDetails/p21ManualUpload`,
        settings,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error?.response?.data?.errors?.[0]?.msg ?? error.message);
    }
  }
);
